import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { analyze, clarify, execute, getConfig } from './mockApi';
import contractsModule from './contracts';

const contracts = contractsModule.default || contractsModule;
const {
  validateProposalResponse,
  validateExecuteResponse,
  TIER,
  EXECUTE_STATUS,
} = contracts;

/**
 * useAiCrew - central state hook for the VYN Crew Call feature.
 * Components remain dumb and consume this hook.
 */
export default function useAiCrew() {
  const [step, setStep] = useState('prompt'); // 'prompt' | 'analyzing' | 'clarifying' | 'proposal' | 'executing' | 'result' | 'answer'
  const [prompt, setPrompt] = useState('');
  const [proposal, setProposal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [crewLeadId, setCrewLeadId] = useState(null);
  const [result, setResult] = useState(null);
  const [answer, setAnswer] = useState(null); // read-only reply (schedule, crew members): nothing to confirm
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [config, setConfig] = useState(null);
  const [backfillNote, setBackfillNote] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [isClarifying, setIsClarifying] = useState(false);

  // Guard against rapid duplicate clicks on execute
  const isExecutingRef = useRef(false);
  // Keep original prompt ref so EXPIRED can re-run analyze accurately
  const originalPromptRef = useRef('');
  // What the last answer was about (a person, a crew); sent with the next question so "and tomorrow?" has a subject.
  const answerMemoryRef = useRef(null);
  // Monotonically increasing run ID to drop stale async results when Start Over (reset) is clicked
  const runIdRef = useRef(0);
  // Ref for clarify smooth transition timer
  const clarifyTimeoutRef = useRef(null);

  // Load config on mount
  useEffect(() => {
    let mounted = true;
    getConfig()
      .then((cfg) => {
        if (mounted && cfg) setConfig(cfg);
      })
      .catch((err) => {
        console.warn('Failed to load Crew Call config:', err);
      });
    return () => {
      mounted = false;
      runIdRef.current += 1;
      clearTimeout(clarifyTimeoutRef.current);
      clarifyTimeoutRef.current = null;
    };
  }, []);

  /**
   * Helper to initialize candidate selections and crew lead from a proposal
   */
  const applyProposal = useCallback((prop) => {
    if (process.env.NODE_ENV !== 'production' && typeof validateProposalResponse === 'function') {
      // transfer/copy proposals have their own shape; checking them against the create-crew shape
      // reported nine false violations on every one
      const validate = prop?.intentType === 'TRANSFER_OWNER' && contracts.validateTransferOwnerResponse
        ? contracts.validateTransferOwnerResponse
        : prop?.intentType === 'COPY_CREW' && contracts.validateCopyCrewResponse
          ? contracts.validateCopyCrewResponse
          : validateProposalResponse;
      // add / remove / lead proposals are a minimal crew+person payload with no validator of their own
      const hasOwnShape = prop?.intentType && prop.intentType !== 'CREATE_CREW'
        && prop.intentType !== 'TRANSFER_OWNER' && prop.intentType !== 'COPY_CREW';
      const violations = hasOwnShape ? [] : validate(prop);
      if (violations && violations.length > 0) {
        console.warn('[contracts:validateProposalResponse in useAiCrew]:', violations);
      }
    }

    setProposal(prop);
    setBackfillNote(null);
    setError(null);
    setErrorCode(null);

    // Initial preselection
    const preselected = new Set();
    (prop.candidates || []).forEach((c) => {
      if (c.preselected) {
        preselected.add(c.employeeId);
      }
    });
    setSelectedIds(preselected);
    setCrewLeadId(prop.crew?.crewLeadId || null);
  }, []);

  /**
   * Run analyze with given or existing prompt
   */
  const runAnalyze = useCallback(
    async (text, options) => {
      const currentRunId = ++runIdRef.current;
      if (clarifyTimeoutRef.current) {
        clearTimeout(clarifyTimeoutRef.current);
        clarifyTimeoutRef.current = null;
      }
      const textToUse = text !== undefined ? text : prompt;
      // a clicked choice sends its own ids (options.selection) instead of making the server read a sentence
      const selection = options && typeof options === 'object' && options.selection ? options.selection : null;
      if (textToUse) {
        originalPromptRef.current = textToUse;
        setPrompt(textToUse);
      }

      setStep('analyzing');
      setIsClarifying(false);
      setError(null);
      setErrorCode(null);
      setResult(null);
      setAnswer(null);

      const timeNow = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const initialTurns = [
        {
          id: 'user-0',
          role: 'user',
          content: textToUse,
          timestamp: timeNow,
        },
      ];
      setConversation(initialTurns);

      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const previous = answerMemoryRef.current;
        const res = await analyze(textToUse, tz, selection ? { selection } : (previous ? { previous } : {}));
        if (runIdRef.current !== currentRunId) return res;
        answerMemoryRef.current = res?.status === 'ANSWER' ? (res.answer?.memory || null) : null;
        if (res?.status === 'ANSWER') {
          setAnswer(res.answer || null);
          setProposal(null);
          setConversation(prev => [...prev, { id: 'agent-' + Date.now(), role: 'agent', content: res.answer?.title || 'Response', subtitle: res.answer?.subtitle }]);
          setStep('answer');
          return res;
        }
        if (res?.status === 'NO_MATCH') {
          setProposal(null); setError(res.message || 'I could not prepare this request. Try adding the missing details or use the manual wizard.');
          setStep('prompt'); return res;
        }
        applyProposal(res);

        const hasClarifications = Boolean(
          res?.status === 'NEEDS_INPUT' ||
          (res?.clarifications && res.clarifications.length > 0)
        );

        if (hasClarifications) {
          setConversation([
            ...initialTurns,
            {
              id: 'agent-0',
              role: 'agent',
              content: 'Let’s fill in the missing details. You can answer one question at a time or include several answers in one reply.',
              crew: res.crew,
              clarifications: res.clarifications || [],
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            },
          ]);
          setStep('clarifying');
        } else {
          setStep('proposal');
        }
        return res;
      } catch (err) {
        if (runIdRef.current !== currentRunId) return;
        console.error('[CrewCall] analyze failed:', err);
        setError(err?.message || 'Failed to analyze request');
        setErrorCode(err?.code || null);
        setStep('prompt');
        throw err;
      }
    },
    [prompt, applyProposal]
  );

  /**
   * Run clarify with conversational text answer without unmounting the screen
   */
  const runClarify = useCallback(
    async (answerInput) => {
      const answerText = typeof answerInput === 'string' ? answerInput : answerInput?.label || 'Selected an option';
      const answerPayload = typeof answerInput === 'string' ? answerInput : answerInput?.answers;
      const currentRunId = ++runIdRef.current;
      if (clarifyTimeoutRef.current) {
        clearTimeout(clarifyTimeoutRef.current);
        clarifyTimeoutRef.current = null;
      }
      // 'none' means nothing was stored (NO_MATCH) so there is nothing to clarify; treat the reply as a new question.
      if (!proposal?.proposalId || proposal.proposalId === 'none') return runAnalyze(answerText);

      const userTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const userTurn = {
        id: 'user-' + Date.now(),
        role: 'user',
        content: answerText,
        timestamp: userTime,
      };

      setConversation((prev) => [...prev, userTurn]);
      setIsClarifying(true);
      setError(null);
      setErrorCode(null);

      try {
        const res = await clarify(proposal.proposalId, answerPayload);
        if (runIdRef.current !== currentRunId) return res;
        if (res?.status === 'ANSWER') {
          setAnswer(res.answer || null); setProposal(null); setSelectedIds(new Set());
          answerMemoryRef.current = res.answer?.memory || null;
          setConversation(prev => [...prev, { id: 'agent-' + Date.now(), role: 'agent', content: res.answer?.message || res.answer?.title }]);
          setStep('answer'); return res;
        }
        if (res?.status === 'NO_MATCH') {
          setProposal(null); setError(res.message || 'This request could not be prepared. Please try again or use the manual wizard.');
          setStep('prompt'); return res;
        }
        applyProposal(res);

        const hasClarifications = Boolean(
          res?.status === 'NEEDS_INPUT' ||
          (res?.clarifications && res.clarifications.length > 0)
        );

        const agentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        if (hasClarifications) {
          setConversation((prev) => [
            ...prev,
            {
              id: 'agent-' + Date.now(),
              role: 'agent',
              content: proposal?.crew?.event?.id && res.crew?.event?.id !== proposal.crew.event.id ? 'The event changed. Please confirm the task and venue for the new event.' : res.clarifications.length === 1 ? 'One detail is still needed. Your other answers are saved.' : 'Your answers are saved. Let’s finish the remaining details.',
              crew: res.crew,
              clarifications: res.clarifications,
              timestamp: agentTime,
            },
          ]);
          setStep('clarifying');
        } else {
          setConversation((prev) => [
            ...prev,
            {
              id: 'agent-' + Date.now(),
              role: 'agent',
              content: 'Your request is ready to review. Check the details and people before confirming; nothing has been scheduled yet.',
              timestamp: agentTime,
            },
          ]);
          // Brief smooth delay so user sees confirmation before viewing full proposal
          clarifyTimeoutRef.current = setTimeout(() => {
            if (runIdRef.current === currentRunId) {
              setStep('proposal');
            }
          }, 450);
        }
        return res;
      } catch (err) {
        if (runIdRef.current !== currentRunId) return;
        console.error('[CrewCall] clarify failed:', err);
        setError(err?.message || 'Failed to clarify request');
        setErrorCode(err?.code || null);
        setStep('clarifying');
        throw err;
      } finally {
        if (runIdRef.current === currentRunId) {
          setIsClarifying(false);
        }
      }
    },
    [proposal, applyProposal, runAnalyze]
  );

  /** A checkbox changes only the worker the user chose. */
  const toggleEmployee = useCallback((employeeId) => {
    if (!proposal?.candidates?.some(c => c.employeeId === employeeId)) return;
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (next.has(employeeId)) next.delete(employeeId); else next.add(employeeId);
      return next;
    });
  }, [proposal]);

  /**
   * Run execute to create crew and schedule
   */
  const runExecute = useCallback(async () => {
    if (isExecutingRef.current || step === 'executing') return;
    if (!proposal?.proposalId) return;
    if (proposal.intentType === 'CREATE_CREW' && selectedIds.size === 0) return;

    const currentRunId = ++runIdRef.current;
    if (clarifyTimeoutRef.current) {
      clearTimeout(clarifyTimeoutRef.current);
      clarifyTimeoutRef.current = null;
    }
    isExecutingRef.current = true;
    setStep('executing');
    setError(null);
    setErrorCode(null);

    try {
      const selectedArray = Array.from(selectedIds);
      const res = await execute(proposal.proposalId, crewLeadId, selectedArray);
      if (runIdRef.current !== currentRunId) return res;

      if (process.env.NODE_ENV !== 'production' && typeof validateExecuteResponse === 'function') {
        const violations = validateExecuteResponse(res);
        if (violations && violations.length > 0) {
          console.warn('[contracts:validateExecuteResponse in useAiCrew]:', violations);
        }
      }

      // Deliberate audit log for developers
      if (res?.traceId) {
        console.log('[CrewCall] execute traceId:', res.traceId);
      }

      // Handle EXPIRED: re-run analyze automatically with stored original prompt
      if (res.status === (EXECUTE_STATUS?.EXPIRED || 'EXPIRED')) {
        setResult(res);
        isExecutingRef.current = false;
        // Automatically re-run analyze with original stored prompt
        await runAnalyze(originalPromptRef.current);
        return;
      }

      // Handle CONFLICT: swap in replacements for conflicted candidates
      if (res.status === (EXECUTE_STATUS?.CONFLICT || 'CONFLICT')) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          (res.conflicts || []).forEach((conf) => {
            if (conf.employeeId) next.delete(conf.employeeId);
          });
          (res.replacements || []).forEach((rep) => {
            if (rep.employeeId) next.add(rep.employeeId);
          });
          return next;
        });
      }

      setResult(res);
      setStep('result');
      return res;
    } catch (err) {
      if (runIdRef.current !== currentRunId) return;
      console.error('[CrewCall] execute failed:', err);
      setError(err?.message || 'Execution failed');
      setErrorCode(err?.code || null);
      setResult({
        status: EXECUTE_STATUS?.FAILED || 'FAILED',
        code: err?.code,
        recoveryRequired: !err?.response && !['UKG_REAUTH_REQUIRED', 'INVALID_REQUEST', 'INVALID_SELECTION', 'UNAUTHORIZED', 'FORBIDDEN'].includes(err?.code)
          || err?.response?.status >= 500,
        message: err?.message || 'The server could not complete crew creation at this time. Your selection has been preserved.',
      });
      setStep('result');
      return { status: EXECUTE_STATUS?.FAILED || 'FAILED' };
    } finally {
      if (runIdRef.current === currentRunId) {
        isExecutingRef.current = false;
      }
    }
  }, [proposal, crewLeadId, selectedIds, step, runAnalyze]);

  /**
   * Reset the whole flow back to initial prompt state
   */
  const reset = useCallback(() => {
    runIdRef.current += 1;
    if (clarifyTimeoutRef.current) {
      clearTimeout(clarifyTimeoutRef.current);
      clarifyTimeoutRef.current = null;
    }
    setStep('prompt');
    setPrompt('');
    answerMemoryRef.current = null;
    setProposal(null);
    setSelectedIds(new Set());
    setCrewLeadId(null);
    setResult(null);
    setAnswer(null);
    setError(null);
    setErrorCode(null);
    setBackfillNote(null);
    setConversation([]);
    setIsClarifying(false);
    isExecutingRef.current = false;
  }, []);

  /**
   * Derived counts for employee selection & shortfall checks
   */
  const counts = useMemo(() => {
    const candidates = proposal?.candidates || [];
    const recommended = candidates.filter((c) => c.tier === (TIER?.RECOMMENDED || 'RECOMMENDED')).length;
    const eligible = candidates.filter((c) => c.tier === (TIER?.ELIGIBLE || 'ELIGIBLE')).length;
    const headcount = proposal?.crew?.headcount || 0;

    return {
      selected: selectedIds.size,
      recommended,
      eligible,
      headcount,
    };
  }, [proposal, selectedIds]);

  return {
    step,
    setStep,
    prompt,
    setPrompt,
    proposal,
    selectedIds,
    setSelectedIds,
    crewLeadId,
    setCrewLeadId,
    result,
    answer,
    error,
    errorCode,
    config,
    backfillNote,
    conversation,
    setConversation,
    isClarifying,
    runAnalyze,
    runClarify,
    runExecute,
    reset,
    toggleEmployee,
    counts,
  };
}
